import { ReactNode, useState } from 'react';
import { Music, Calendar, BarChart3, Users, Menu, X } from 'lucide-react';
import { useApp } from '../lib/AppContext';
import AdminHeader from './AdminHeader';

interface AdminShellProps {
  children: ReactNode;
  activeSection?: 'songs' | 'weekly-cards' | 'users' | 'analytics';
}

export default function AdminShell({ children, activeSection }: AdminShellProps) {
  const { navigateTo } = useApp();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sections = [
    { id: 'analytics', label: 'Analytics', icon: BarChart3, route: 'admin-analytics' },
    { id: 'songs', label: 'Songs', icon: Music, route: 'admin-songs' },
    { id: 'weekly-cards', label: 'Weekly Cards', icon: Calendar, route: 'admin-weekly-cards' },
    { id: 'users', label: 'Users', icon: Users, route: 'admin-users' },
  ];

  const handleNavigation = (route: string) => {
    navigateTo(route as any);
    setMobileMenuOpen(false);
  };

  return (
    <div className="min-h-screen bg-[#111111] flex flex-col">
      <AdminHeader onMenuToggle={() => setMobileMenuOpen(!mobileMenuOpen)} />

      <div className="flex flex-1 bg-[#111111] relative">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:block w-64 border-r border-[#282828] bg-[#111111] flex-shrink-0">
          <nav className="p-4 space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  onClick={() => navigateTo(section.route as any)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border border-[#40E0D0]/30'
                      : 'text-[#A0A0A0] hover:bg-[#181818] hover:text-[#F5F5F5]'
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                  <span className="text-[15px] font-medium">{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        {/* Mobile Drawer Overlay */}
        {mobileMenuOpen && (
          <div
            className="lg:hidden fixed inset-0 bg-black/60 z-40"
            onClick={() => setMobileMenuOpen(false)}
          />
        )}

        {/* Mobile Drawer */}
        <aside
          className={`lg:hidden fixed top-16 left-0 bottom-0 w-64 bg-[#111111] border-r border-[#282828] z-50 transform transition-transform duration-300 ${
            mobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
          }`}
        >
          <nav className="p-4 space-y-1">
            {sections.map((section) => {
              const Icon = section.icon;
              const isActive = activeSection === section.id;

              return (
                <button
                  key={section.id}
                  onClick={() => handleNavigation(section.route)}
                  className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-all ${
                    isActive
                      ? 'bg-[#40E0D0]/10 text-[#40E0D0] border border-[#40E0D0]/30'
                      : 'text-[#A0A0A0] hover:bg-[#181818] hover:text-[#F5F5F5]'
                  }`}
                >
                  <Icon className="w-5 h-5" strokeWidth={2} />
                  <span className="text-[15px] font-medium">{section.label}</span>
                </button>
              );
            })}
          </nav>
        </aside>

        <main className="flex-1 overflow-auto w-full">
          {children}
        </main>
      </div>
    </div>
  );
}
